import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Circle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Props {
  lat: number | null | undefined;
  lng: number | null | undefined;
  status?: 'online' | 'busy' | 'offline' | string;
  name?: string;
  lastLocationAt?: string | null;
  lastActiveAddress?: string | null;
}

const markerIcon = (status: string) => {
  const color = status === 'busy' ? '#f59e0b' : status === 'online' ? '#16a34a' : '#64748b';
  return L.divIcon({
    className: 'zipto-driver-marker',
    html: `
      <div style="position:relative;width:30px;height:30px">
        <span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.25;
          ${status === 'online' ? 'animation:ziptoPulse 1.8s ease-out infinite;' : ''}"></span>
        <span style="position:absolute;top:5px;left:5px;width:20px;height:20px;border-radius:50%;
          background:${color};border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);
          display:flex;align-items:center;justify-content:center;font-size:11px">🛵</span>
      </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
};

const statusMeta = (status?: string) => {
  if (status === 'online') return { label: 'Online now', cls: 'bg-emerald-100 text-emerald-700', dot: 'text-emerald-500' };
  if (status === 'busy') return { label: 'On a trip', cls: 'bg-amber-100 text-amber-700', dot: 'text-amber-500' };
  return { label: 'Offline', cls: 'bg-slate-100 text-slate-600', dot: 'text-slate-400' };
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function absoluteTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export function DriverLocationMap({ lat, lng, status = 'offline', name, lastLocationAt, lastActiveAddress }: Props) {
  const hasLocation = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  const meta = statusMeta(status);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Navigation className="h-5 w-5 text-primary" />
          Live Location
        </CardTitle>
        <Badge className={meta.cls}>
          <Circle className={`h-2 w-2 mr-1.5 fill-current ${meta.dot}`} />
          {meta.label}
        </Badge>
      </CardHeader>

      {/* Last Active info bar */}
      {lastLocationAt && (
        <div className="mx-4 mb-3 flex flex-col sm:flex-row sm:items-center gap-1.5 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-sm font-semibold shrink-0">
            <Clock className="h-3.5 w-3.5 text-primary" />
            <span>Last Active:</span>
            <span className="text-primary">{timeAgo(lastLocationAt)}</span>
          </div>
          <span className="hidden sm:inline text-muted-foreground/40 mx-1">·</span>
          <span className="text-xs text-muted-foreground">{absoluteTime(lastLocationAt)}</span>
          {lastActiveAddress && (
            <>
              <span className="hidden sm:inline text-muted-foreground/40 mx-1">·</span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
                <MapPin className="h-3 w-3 shrink-0 text-primary/70" />
                <span className="truncate" title={lastActiveAddress}>{lastActiveAddress}</span>
              </div>
            </>
          )}
        </div>
      )}

      <CardContent className="p-0">
        {hasLocation ? (
          <div className="h-[340px]">
            <MapContainer
              center={[lat as number, lng as number]}
              zoom={15}
              scrollWheelZoom
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[lat as number, lng as number]} icon={markerIcon(status)}>
                <Popup>
                  <div className="text-sm font-medium">{name || 'Driver'}</div>
                  <div className="text-xs text-muted-foreground">{meta.label}</div>
                  {lastLocationAt && (
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Last seen: {timeAgo(lastLocationAt)}
                    </div>
                  )}
                  {lastActiveAddress && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 max-w-[180px]">
                      {lastActiveAddress}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {(lat as number).toFixed(5)}, {(lng as number).toFixed(5)}
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
            <MapPin className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No location available</p>
            <p className="text-xs">
              {lastLocationAt
                ? `Last seen ${timeAgo(lastLocationAt)}`
                : `The driver hasn't shared a GPS location yet${status !== 'online' ? ' (currently offline).' : '.'}`}
            </p>
            {lastActiveAddress && (
              <p className="text-xs mt-1 text-muted-foreground/70">{lastActiveAddress}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}